/**
 * <custom-form-fields>
 *
 * Companion element for the "Custom Product Fields" product block.
 *
 * Responsibilities:
 *   1. Validate required fields before the product form submits.
 *   2. Provide a drag-and-drop image upload zone with empty / loading /
 *      preview / error states; optionally open a 1:1 crop dialog, then
 *      upload files to a remote service and write the returned key into
 *      a hidden line-item-property input that is submitted with the form.
 *   3. Block submission while uploads are in flight; auto-resubmit
 *      once they finish if the user already attempted to submit.
 *   4. Optionally collect a customer email (not a line item property)
 *      for checkout prefill and the order-record webhook.
 *   5. After add-to-cart, POST a JSON cart summary to the configured
 *      Order Record Endpoint, then return a checkout URL that may
 *      include `checkout[email]`.
 *
 * Inputs are NOT nested inside the product `<form>` itself; they
 * are linked via the `form="<product_form_id>"` attribute. Submission
 * is intercepted on the form element using a capture-phase listener
 * so we run *before* the existing <product-form> handler.
 */
if (!customElements.get("custom-form-fields")) {
  customElements.define(
    "custom-form-fields",
    class CustomFormFields extends HTMLElement {
      constructor() {
        super();
        this.pendingUploads = 0;
        this.submitPending = false;
        this.boundOnFormSubmit = this.onFormSubmit.bind(this);
        // Map<dropzoneEl, objectUrl> for revoking blob URLs on cleanup.
        this.objectUrls = new WeakMap();
        this.maxUploadBytes = 5 * 1024 * 1024;
        this.maxCropOutput = 2048;
        this.cropSession = null;
        this.boundOnCropResize = this.onCropResize.bind(this);
      }

      connectedCallback() {
        this.formId = this.getAttribute("data-form-id") || "";
        this.uploadUrl =
          this.getAttribute("data-upload-url") || "/api/public/media/upload";
        this.uploadingText =
          this.getAttribute("data-uploading-text") ||
          "Bild wird hochgeladen…";

        // Delegated submit listener on document. The form id is deterministic
        // (`product-form-<section.id>`) so we just match by id at submit time.
        // This also means we don't care whether the form exists yet when the
        // <custom-form-fields> element initializes — block render order in
        // the theme editor is irrelevant.
        document.addEventListener("submit", this.boundOnFormSubmit, true);

        // Keep the public-mirror input (visible-in-cart property) in sync
        // with the source input value as the user types / changes selection.
        this.addEventListener("input", this.onAnyFieldInput);
        this.addEventListener("change", this.onAnyFieldInput);

        this.querySelectorAll("[data-dropzone]").forEach((dropzone) => {
          this.initDropzone(dropzone);
        });

        this.initCropDialog();
      }

      onAnyFieldInput = (event) => {
        if (event.target.matches("[data-public-mirror]")) return;
        const field = event.target.closest(".custom-form-fields__field");
        if (field) this.syncPublicMirror(field);
      };

      // Mirrors the source input's value into the optional `[data-public-mirror]`
      // hidden input. The mirror is disabled when value is empty so it is not
      // submitted (keeps the cart free of empty visible properties).
      syncPublicMirror(field) {
        if (!field) return;
        const mirror = field.querySelector("[data-public-mirror]");
        if (!mirror) return;

        const fieldType = field.getAttribute("data-field-type");
        let value = "";

        if (fieldType === "image") {
          const target = field.querySelector("[data-upload-target]");
          value = target && !target.disabled ? target.value : "";
        } else {
          const source = field.querySelector(
            "input:not([data-public-mirror]):not([type=file]), select, textarea"
          );
          value = source ? String(source.value || "") : "";
        }

        mirror.value = value;
        mirror.disabled = value.trim() === "";
      }

      disconnectedCallback() {
        document.removeEventListener("submit", this.boundOnFormSubmit, true);
        window.removeEventListener("resize", this.boundOnCropResize);
        if (this.cropSession) this.closeCropDialog();
      }

      getForm() {
        return this.formId ? document.getElementById(this.formId) : null;
      }

      // ----- Form submit interception -----------------------------------------

      onFormSubmit(event) {
        const targetId = event.target.getAttribute('id');
        if (targetId !== this.formId) return;

        if (this.pendingUploads > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.submitPending = true;
          this.setSubmitButtonLoading(true);
          return;
        }

        if (!this.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }

      validate() {
        let firstInvalid = null;
        let valid = true;

        this.querySelectorAll(".custom-form-fields__field").forEach((field) => {
          const required = field.getAttribute("data-required") === "true";
          const errorEl = field.querySelector("[data-field-error]");
          const fieldType = field.getAttribute("data-field-type");

          if (errorEl) {
            errorEl.hidden = true;
            errorEl.textContent = "";
          }
          field.classList.remove("custom-form-fields__field--invalid");

          if (!required) return;

          let value = "";
          if (fieldType === "image") {
            const target = field.querySelector("[data-upload-target]");
            value = target && !target.disabled ? target.value : "";
          } else {
            const input = field.querySelector(
              "input:not([data-public-mirror]):not([type=file]), select, textarea"
            );
            value = input ? String(input.value || "").trim() : "";
          }

          if (!value) {
            valid = false;
            field.classList.add("custom-form-fields__field--invalid");
            if (errorEl) {
              errorEl.textContent =
                fieldType === "image"
                  ? "Please upload a file."
                  : "This field is required.";
              errorEl.hidden = false;
            }
            if (!firstInvalid) firstInvalid = field;
            return;
          }

          if (fieldType === "email") {
            const emailInput = field.querySelector("[data-order-email]");
            if (emailInput && !emailInput.checkValidity()) {
              valid = false;
              field.classList.add("custom-form-fields__field--invalid");
              if (errorEl) {
                errorEl.textContent = "Please enter a valid email address.";
                errorEl.hidden = false;
              }
              if (!firstInvalid) firstInvalid = field;
            }
          }
        });

        if (firstInvalid) {
          const focusable = firstInvalid.querySelector(
            "input:not([type=hidden]):not([type=file]), select, textarea, [data-dropzone]"
          );
          if (focusable) focusable.focus();
        }

        return valid;
      }

      getCollectedEmail() {
        const input = this.querySelector("[data-order-email]");
        return input ? String(input.value || "").trim() : "";
      }

      buildCheckoutUrl() {
        const base = this.getAttribute("data-checkout-url") || "/checkout";
        const email = this.getCollectedEmail();
        if (!email) return base;

        const params = new URLSearchParams();
        params.set("checkout[email]", email);
        const separator = base.indexOf("?") === -1 ? "?" : "&";
        return `${base}${separator}${params.toString()}`;
      }

      async fetchCart() {
        const cartUrl =
          window.routes && window.routes.cart_url
            ? `${window.routes.cart_url}.js`
            : "/cart.js";
        const response = await fetch(cartUrl, {
          method: "GET",
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          throw new Error(`cart.js failed (HTTP ${response.status})`);
        }
        return response.json();
      }

      async postOrderRecord(endpoint, payload) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);
        try {
          const response = await fetch(endpoint, {
            method: "POST",
            mode: "cors",
            credentials: "omit",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(payload),
            signal: controller.signal,
          });
          if (!response.ok) {
            throw new Error(`Order record failed (HTTP ${response.status})`);
          }
        } finally {
          clearTimeout(timeoutId);
        }
      }

      async recordOrder() {
        const endpoint = (
          this.getAttribute("data-order-record-endpoint") || ""
        ).trim();
        if (!endpoint) return;

        try {
          const cart = await this.fetchCart();
          const productId = Number(this.getAttribute("data-product-id"));
          await this.postOrderRecord(endpoint, {
            email: this.getCollectedEmail() || null,
            cart,
            product_id: Number.isFinite(productId) ? productId : null,
            product_handle: this.getAttribute("data-product-handle") || null,
            shop:
              this.getAttribute("data-shop") ||
              (window.Shopify && window.Shopify.shop) ||
              null,
          });
        } catch (err) {
          console.error("[custom-form-fields] order record failed", err);
        }
      }

      async beforeCheckout() {
        await this.recordOrder();
        return this.buildCheckoutUrl();
      }

      // ----- Dropzone setup ---------------------------------------------------

      initDropzone(dropzone) {
        const fileInput = dropzone.querySelector("[data-upload-input]");
        const removeButton = dropzone.querySelector("[data-dropzone-remove]");

        if (!fileInput) return;

        // Click anywhere on the dropzone (except the remove button) opens
        // the native file picker.
        dropzone.addEventListener("click", (event) => {
          if (event.target.closest("[data-dropzone-remove]")) return;
          if (event.target.closest("[data-upload-input]")) return;
          if (dropzone.dataset.state === "loading") return;
          event.preventDefault();
          this.openFilePicker(fileInput);
        });

        // Keyboard: Space / Enter on the focused dropzone opens picker.
        dropzone.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          if (dropzone.dataset.state === "loading") return;
          event.preventDefault();
          this.openFilePicker(fileInput);
        });

        fileInput.addEventListener("change", () => {
          const file = fileInput.files && fileInput.files[0];
          if (file) this.onFileChosen(dropzone, file);
        });

        // Native HTML5 drag and drop.
        ["dragenter", "dragover"].forEach((type) => {
          dropzone.addEventListener(type, (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (dropzone.dataset.state === "loading") return;
            dropzone.classList.add("custom-form-fields__dropzone--dragover");
          });
        });

        ["dragleave", "dragend"].forEach((type) => {
          dropzone.addEventListener(type, (event) => {
            event.preventDefault();
            event.stopPropagation();
            // Only remove the dragover class if we're truly leaving the
            // dropzone (not entering a child element).
            if (
              type === "dragleave" &&
              dropzone.contains(event.relatedTarget)
            ) {
              return;
            }
            dropzone.classList.remove("custom-form-fields__dropzone--dragover");
          });
        });

        dropzone.addEventListener("drop", (event) => {
          event.preventDefault();
          event.stopPropagation();
          dropzone.classList.remove("custom-form-fields__dropzone--dragover");

          if (dropzone.dataset.state === "loading") return;

          const file =
            event.dataTransfer &&
            event.dataTransfer.files &&
            event.dataTransfer.files[0];
          if (!file) return;

          // Reflect the dropped file in the underlying file input so it
          // mirrors what a click-to-pick would have produced.
          try {
            const dt = new DataTransfer();
            dt.items.add(file);
            fileInput.files = dt.files;
          } catch (e) {
            // DataTransfer constructor not supported — non-fatal.
          }

          this.onFileChosen(dropzone, file);
        });

        if (removeButton) {
          removeButton.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.resetDropzone(dropzone);
          });
        }
      }

      // ----- Dropzone state ---------------------------------------------------

      setDropzoneState(dropzone, state, errorText) {
        dropzone.dataset.state = state;

        const empty = dropzone.querySelector("[data-dropzone-empty]");
        const loading = dropzone.querySelector("[data-dropzone-loading]");
        const preview = dropzone.querySelector("[data-dropzone-preview]");
        const error = dropzone.querySelector("[data-dropzone-error]");

        if (empty) empty.hidden = state !== "empty";
        if (loading) loading.hidden = state !== "loading";
        if (preview) preview.hidden = state !== "preview";
        if (error) {
          error.hidden = state !== "error";
          if (state === "error") {
            const text = error.querySelector("[data-dropzone-error-text]");
            if (text) {
              text.textContent =
                errorText ||
                dropzone.dataset.errorText ||
                "Upload failed. Please try again.";
            }
          }
        }

        const field = dropzone.closest(".custom-form-fields__field");
        if (field) {
          field.classList.toggle(
            "custom-form-fields__field--invalid",
            state === "error"
          );
        }
      }

      resetDropzone(dropzone) {
        const fileInput = dropzone.querySelector("[data-upload-input]");
        const target = dropzone.querySelector("[data-upload-target]");
        const previewImg = dropzone.querySelector("[data-dropzone-image]");

        if (fileInput) fileInput.value = "";
        if (target) {
          target.value = "";
          target.disabled = true;
        }

        const url = this.objectUrls.get(dropzone);
        if (url) {
          URL.revokeObjectURL(url);
          this.objectUrls.delete(dropzone);
        }
        if (previewImg) {
          previewImg.removeAttribute("src");
          previewImg.alt = "";
        }

        const field = dropzone.closest(".custom-form-fields__field");
        const errorEl = field && field.querySelector("[data-field-error]");
        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = "";
        }

        this.syncPublicMirror(field);
        this.setDropzoneState(dropzone, "empty");
      }

      onFileChosen(dropzone, file) {
        if (dropzone.getAttribute("data-crop") === "true") {
          this.openCropDialog(dropzone, file);
          return;
        }
        this.handleFile(dropzone, file);
      }

      // ----- 1:1 crop dialog --------------------------------------------------

      initCropDialog() {
        const dialog = this.querySelector("[data-crop-dialog]");
        if (!dialog) return;

        this.cropDialog = dialog;
        this.cropStage = dialog.querySelector("[data-crop-stage]");
        this.cropFrame = dialog.querySelector("[data-crop-frame]");
        this.cropImage = dialog.querySelector("[data-crop-image]");
        this.cropCancel = dialog.querySelector("[data-crop-cancel]");
        this.cropConfirm = dialog.querySelector("[data-crop-confirm]");

        dialog.addEventListener("cancel", (event) => {
          event.preventDefault();
          this.cancelCrop();
        });
        if (this.cropCancel) {
          this.cropCancel.addEventListener("click", () => this.cancelCrop());
        }
        if (this.cropConfirm) {
          this.cropConfirm.addEventListener("click", () => this.confirmCrop());
        }
        if (this.cropStage) {
          this.cropStage.addEventListener("pointerdown", this.onCropPointerDown);
          this.cropStage.addEventListener("pointermove", this.onCropPointerMove);
          this.cropStage.addEventListener("pointerup", this.onCropPointerUp);
          this.cropStage.addEventListener(
            "pointercancel",
            this.onCropPointerUp
          );
          this.cropStage.addEventListener("wheel", this.onCropWheel, {
            passive: false,
          });
        }
        window.addEventListener("resize", this.boundOnCropResize);
      }

      async openCropDialog(dropzone, file) {
        if (!this.cropDialog || !this.cropImage || !this.cropStage) {
          this.handleFile(dropzone, file);
          return;
        }
        if (!file.type || !file.type.startsWith("image/")) {
          this.handleFile(dropzone, file);
          return;
        }

        if (this.cropSession) this.closeCropDialog();

        try {
          const bitmap = await this.loadOrientedImage(file);
          this.cropSession = {
            dropzone,
            file,
            bitmap,
            imageWidth: 1,
            imageHeight: 1,
            sourceRatio: 1,
            view: 0,
            scale: 1,
            minScale: 1,
            maxScale: 4,
            x: 0,
            y: 0,
            pointers: new Map(),
            lastPinchDistance: 0,
          };
          if (this.cropConfirm) this.cropConfirm.disabled = false;
          this.lockBodyScroll();
          this.cropDialog.showModal();
          const preview = await this.paintCropPreview(bitmap);
          if (!this.cropSession) return;
          this.cropSession.imageWidth = preview.imageWidth;
          this.cropSession.imageHeight = preview.imageHeight;
          this.cropSession.sourceRatio = preview.sourceRatio;
          await this.resetCropView();
        } catch (err) {
          console.error("[custom-form-fields] crop open failed", err);
          this.closeCropDialog();
          this.handleFile(dropzone, file);
        }
      }

      async paintCropPreview(image) {
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        const maxPreview = 1600;
        const previewScale = Math.min(
          1,
          maxPreview / Math.max(width, height)
        );
        const previewWidth = Math.max(1, Math.round(width * previewScale));
        const previewHeight = Math.max(1, Math.round(height * previewScale));
        const canvas = document.createElement("canvas");
        canvas.width = previewWidth;
        canvas.height = previewHeight;
        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Image processing is not supported in this browser.");
        }
        context.drawImage(image, 0, 0, previewWidth, previewHeight);
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((result) => {
            if (!result) {
              reject(new Error("Could not prepare the selected image."));
              return;
            }
            resolve(result);
          }, "image/jpeg", 0.92);
        });
        if (this.cropPreviewUrl) URL.revokeObjectURL(this.cropPreviewUrl);
        this.cropPreviewUrl = URL.createObjectURL(blob);
        await this.setCropImageSrc(this.cropPreviewUrl);
        return {
          imageWidth: previewWidth,
          imageHeight: previewHeight,
          sourceRatio: width / previewWidth,
        };
      }

      setCropImageSrc(url) {
        return new Promise((resolve, reject) => {
          if (!this.cropImage) {
            reject(new Error("Could not prepare the selected image."));
            return;
          }
          let settled = false;
          const finish = (err) => {
            if (settled) return;
            settled = true;
            this.cropImage.onload = null;
            this.cropImage.onerror = null;
            if (err) reject(err);
            else resolve();
          };
          this.cropImage.onload = () => finish();
          this.cropImage.onerror = () =>
            finish(new Error("Could not read the selected image."));
          this.cropImage.src = url;
          if (this.cropImage.complete && this.cropImage.naturalWidth) {
            finish();
          }
        });
      }

      nextFrame() {
        return new Promise((resolve) => requestAnimationFrame(resolve));
      }

      async resetCropView(attempt = 0) {
        const session = this.cropSession;
        if (!session || !this.cropFrame || !this.cropImage) return;
        await this.nextFrame();
        this.measureCropView();
        if (session.view < 8 && attempt < 12) {
          await this.resetCropView(attempt + 1);
          return;
        }
        if (session.view < 8) {
          session.view = Math.max(
            80,
            Math.min(window.innerWidth - 32, window.innerHeight * 0.55)
          );
        }
        this.updateCropScaleRange();
        session.scale = session.minScale;
        session.x = (session.view - session.imageWidth * session.scale) / 2;
        session.y = (session.view - session.imageHeight * session.scale) / 2;
        this.clampCrop();
        this.applyCropTransform();
      }

      measureCropView() {
        const session = this.cropSession;
        if (!session || !this.cropFrame) return;
        session.view = Math.min(
          this.cropFrame.clientWidth,
          this.cropFrame.clientHeight
        );
        this.updateCropScaleRange();
      }

      updateCropScaleRange() {
        const session = this.cropSession;
        if (!session || session.view < 8) return;
        session.minScale = Math.max(
          session.view / session.imageWidth,
          session.view / session.imageHeight
        );
        session.maxScale = session.minScale * 4;
      }

      clampCrop() {
        const session = this.cropSession;
        if (!session) return;
        session.scale = Math.min(
          session.maxScale,
          Math.max(session.minScale, session.scale)
        );
        const width = session.imageWidth * session.scale;
        const height = session.imageHeight * session.scale;
        session.x = Math.min(0, Math.max(session.view - width, session.x));
        session.y = Math.min(0, Math.max(session.view - height, session.y));
      }

      applyCropTransform() {
        const session = this.cropSession;
        if (!session || !this.cropImage) return;
        this.cropImage.style.width = `${session.imageWidth}px`;
        this.cropImage.style.height = `${session.imageHeight}px`;
        this.cropImage.style.transform = `translate(${session.x}px, ${session.y}px) scale(${session.scale})`;
      }

      zoomCropAt(viewX, viewY, factor) {
        const session = this.cropSession;
        if (!session) return;
        const nextScale = Math.min(
          session.maxScale,
          Math.max(session.minScale, session.scale * factor)
        );
        if (nextScale === session.scale) return;
        const imageX = (viewX - session.x) / session.scale;
        const imageY = (viewY - session.y) / session.scale;
        session.scale = nextScale;
        session.x = viewX - imageX * nextScale;
        session.y = viewY - imageY * nextScale;
        this.clampCrop();
        this.applyCropTransform();
      }

      viewportPoint(event) {
        const rect = this.cropFrame.getBoundingClientRect();
        return {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };
      }

      pinchDistance() {
        const session = this.cropSession;
        if (!session || session.pointers.size < 2) return 0;
        const points = Array.from(session.pointers.values());
        const dx = points[0].x - points[1].x;
        const dy = points[0].y - points[1].y;
        return Math.hypot(dx, dy);
      }

      pinchCenter() {
        const session = this.cropSession;
        const points = Array.from(session.pointers.values());
        return {
          x: (points[0].x + points[1].x) / 2,
          y: (points[0].y + points[1].y) / 2,
        };
      }

      onCropPointerDown = (event) => {
        const session = this.cropSession;
        if (!session || !this.cropStage) return;
        event.preventDefault();
        this.cropStage.setPointerCapture(event.pointerId);
        const point = this.viewportPoint(event);
        session.pointers.set(event.pointerId, point);
        if (session.pointers.size === 2) {
          session.lastPinchDistance = this.pinchDistance();
        }
      };

      onCropPointerMove = (event) => {
        const session = this.cropSession;
        if (!session || !session.pointers.has(event.pointerId)) return;
        event.preventDefault();
        const next = this.viewportPoint(event);
        const previous = session.pointers.get(event.pointerId);
        session.pointers.set(event.pointerId, next);

        if (session.pointers.size === 2) {
          const distance = this.pinchDistance();
          if (session.lastPinchDistance > 0 && distance > 0) {
            const center = this.pinchCenter();
            this.zoomCropAt(
              center.x,
              center.y,
              distance / session.lastPinchDistance
            );
          }
          session.lastPinchDistance = distance;
          return;
        }

        session.x += next.x - previous.x;
        session.y += next.y - previous.y;
        this.clampCrop();
        this.applyCropTransform();
      };

      onCropPointerUp = (event) => {
        const session = this.cropSession;
        if (!session) return;
        session.pointers.delete(event.pointerId);
        session.lastPinchDistance =
          session.pointers.size === 2 ? this.pinchDistance() : 0;
      };

      onCropWheel = (event) => {
        if (!this.cropSession) return;
        event.preventDefault();
        const point = this.viewportPoint(event);
        const factor = event.deltaY > 0 ? 0.92 : 1.08;
        this.zoomCropAt(point.x, point.y, factor);
      };

      onCropResize() {
        if (!this.cropSession || !this.cropDialog || !this.cropDialog.open) {
          return;
        }
        this.measureCropView();
        this.clampCrop();
        this.applyCropTransform();
      }

      cancelCrop() {
        const session = this.cropSession;
        const fileInput =
          session && session.dropzone.querySelector("[data-upload-input]");
        this.closeCropDialog();
        if (fileInput) fileInput.value = "";
      }

      async confirmCrop() {
        const session = this.cropSession;
        if (!session) return;
        if (this.cropConfirm) this.cropConfirm.disabled = true;
        try {
          const file = await this.exportCroppedFile();
          const dropzone = session.dropzone;
          this.closeCropDialog();
          this.handleFile(dropzone, file);
        } catch (err) {
          console.error("[custom-form-fields] crop export failed", err);
          if (this.cropConfirm) this.cropConfirm.disabled = false;
        }
      }

      async exportCroppedFile() {
        const session = this.cropSession;
        if (!session) {
          throw new Error("Could not crop the selected image.");
        }
        this.measureCropView();
        this.clampCrop();

        const srcX = (-session.x / session.scale) * session.sourceRatio;
        const srcY = (-session.y / session.scale) * session.sourceRatio;
        const srcSize = (session.view / session.scale) * session.sourceRatio;
        const image = session.bitmap;
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        const safeSize = Math.min(srcSize, width, height);
        const safeX = Math.min(Math.max(0, srcX), Math.max(0, width - safeSize));
        const safeY = Math.min(
          Math.max(0, srcY),
          Math.max(0, height - safeSize)
        );
        const outputSize = Math.max(
          1,
          Math.min(this.maxCropOutput, Math.round(safeSize))
        );

        const canvas = document.createElement("canvas");
        canvas.width = outputSize;
        canvas.height = outputSize;
        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Image processing is not supported in this browser.");
        }
        context.drawImage(
          image,
          safeX,
          safeY,
          safeSize,
          safeSize,
          0,
          0,
          outputSize,
          outputSize
        );

        const outputType =
          session.file.type === "image/png" ||
          session.file.type === "image/jpeg" ||
          session.file.type === "image/webp"
            ? session.file.type
            : "image/jpeg";

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (result) => {
              if (!result) {
                reject(new Error("Could not crop the selected image."));
                return;
              }
              resolve(result);
            },
            outputType,
            0.92
          );
        });

        return this.fileFromBlob(blob, session.file.name, outputType);
      }

      closeCropDialog() {
        const session = this.cropSession;
        this.cropSession = null;
        if (this.cropDialog && this.cropDialog.open) this.cropDialog.close();
        this.unlockBodyScroll();
        if (this.cropConfirm) this.cropConfirm.disabled = false;
        if (this.cropPreviewUrl) {
          URL.revokeObjectURL(this.cropPreviewUrl);
          this.cropPreviewUrl = "";
        }
        if (this.cropImage) {
          this.cropImage.onload = null;
          this.cropImage.onerror = null;
          this.cropImage.removeAttribute("src");
          this.cropImage.style.transform = "";
          this.cropImage.style.width = "";
          this.cropImage.style.height = "";
        }
        if (session && session.bitmap && typeof session.bitmap.close === "function") {
          session.bitmap.close();
        }
      }

      lockBodyScroll() {
        if (this.bodyScrollLocked) return;
        this.bodyScrollLocked = true;
        this.previousBodyOverflow = document.body.style.overflow;
        this.previousRootOverflow = document.documentElement.style.overflow;
        this.previousBodyTouchAction = document.body.style.touchAction;
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";
        document.body.style.touchAction = "none";
      }

      unlockBodyScroll() {
        if (!this.bodyScrollLocked) return;
        this.bodyScrollLocked = false;
        document.body.style.overflow = this.previousBodyOverflow || "";
        document.documentElement.style.overflow =
          this.previousRootOverflow || "";
        document.body.style.touchAction = this.previousBodyTouchAction || "";
      }

      async loadOrientedImage(file) {
        if (typeof createImageBitmap === "function") {
          try {
            return await createImageBitmap(file, {
              imageOrientation: "from-image",
            });
          } catch (e) {
            try {
              return await createImageBitmap(file);
            } catch (e2) {
              // Fall through to Image() for older browsers.
            }
          }
        }
        return this.loadImageFromFile(file);
      }

      async handleFile(dropzone, file) {
        const target = dropzone.querySelector("[data-upload-target]");
        const previewImg = dropzone.querySelector("[data-dropzone-image]");
        const field = dropzone.closest(".custom-form-fields__field");
        const errorEl = field && field.querySelector("[data-field-error]");

        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = "";
        }

        if (target) {
          target.value = "";
          target.disabled = true;
        }

        // Show a local preview as soon as the file is picked/dropped.
        const previousUrl = this.objectUrls.get(dropzone);
        if (previousUrl) URL.revokeObjectURL(previousUrl);

        const objectUrl = URL.createObjectURL(file);
        this.objectUrls.set(dropzone, objectUrl);
        if (previewImg) {
          previewImg.src = objectUrl;
          previewImg.alt = file.name;
        }

        this.setDropzoneState(dropzone, "loading");
        this.beginUpload();

        try {
          const fileForUpload = await this.prepareFileForUpload(file);
          const key = await this.uploadFile(fileForUpload);
          if (target) {
            target.value = key;
            target.disabled = false;
          }
          this.syncPublicMirror(field);
          this.setDropzoneState(dropzone, "preview");
        } catch (err) {
          console.error("[custom-form-fields] upload failed", err);
          // Revoke the local preview URL on failure.
          const url = this.objectUrls.get(dropzone);
          if (url) {
            URL.revokeObjectURL(url);
            this.objectUrls.delete(dropzone);
          }
          if (previewImg) previewImg.removeAttribute("src");

          const fileInput = dropzone.querySelector("[data-upload-input]");
          if (fileInput) fileInput.value = "";

          this.syncPublicMirror(field);
          const errorText =
            err && typeof err.message === "string" && err.message
              ? err.message
              : undefined;
          this.setDropzoneState(dropzone, "error", errorText);
        } finally {
          this.endUpload();
        }
      }

      // ----- Upload + submit-button helpers -----------------------------------

      openFilePicker(fileInput) {
        // Reset value so selecting the same file still triggers `change`.
        fileInput.value = "";
        try {
          if (typeof fileInput.showPicker === "function") {
            fileInput.showPicker();
            return;
          }
        } catch (e) {
          // Fall through to `click()` for browsers with guarded showPicker.
        }
        fileInput.click();
      }

      async prepareFileForUpload(file) {
        if (!file || file.size <= this.maxUploadBytes) return file;
        if (!file.type || !file.type.startsWith("image/")) {
          ThemeAnalytics.logError("Only image files up to 4MB are supported.");
          throw new Error("Only image files up to 4MB are supported.");
        }
        return this.resizeImageToLimit(file, this.maxUploadBytes);
      }

      async resizeImageToLimit(file, maxBytes) {
        const originalSize = file.size;
        const image = await this.loadImageFromFile(file);
        const outputType =
          file.type === "image/png" ||
          file.type === "image/jpeg" ||
          file.type === "image/webp"
            ? file.type
            : "image/jpeg";

        const initialScale = Math.min(
          1,
          Math.sqrt(maxBytes / file.size) * 0.98
        );
        let width = Math.max(1, Math.floor(image.naturalWidth * initialScale));
        let height = Math.max(1, Math.floor(image.naturalHeight * initialScale));
        let quality = 0.9;

        // Iterate quality and dimensions so we can reliably enforce <= 4MB.
        for (let attempt = 0; attempt < 14; attempt += 1) {
          const blob = await this.renderResizedImageBlob(
            image,
            width,
            height,
            outputType,
            quality
          );
          if (blob.size <= maxBytes) {
            ThemeAnalytics.logInfo("Image resized successfully", {
              originalSize,
              newSize: blob.size,
            });
            return this.fileFromBlob(blob, file.name, outputType);
          }

          const canLowerQuality =
            outputType !== "image/png" && quality > 0.45;
          if (canLowerQuality) {
            quality = Math.max(0.45, quality - 0.1);
            continue;
          }

          if (width <= 320 || height <= 320) break;
          width = Math.max(1, Math.floor(width * 0.85));
          height = Math.max(1, Math.floor(height * 0.85));
          quality = 0.9;
        }
        
        ThemeAnalytics.logError("Image is too large to optimize below 4MB. Please choose a smaller image.");
        throw new Error(
          "Image is too large to optimize below 4MB. Please choose a smaller image."
        );
      }

      loadImageFromFile(file) {
        return new Promise((resolve, reject) => {
          const url = URL.createObjectURL(file);
          const image = new Image();
          image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
          };
          image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Could not read the selected image."));
          };
          image.src = url;
        });
      }

      renderResizedImageBlob(image, width, height, mimeType, quality) {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Image processing is not supported in this browser.");
        }

        context.drawImage(image, 0, 0, width, height);

        return new Promise((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Could not optimize the selected image."));
                return;
              }
              resolve(blob);
            },
            mimeType,
            quality
          );
        });
      }

      fileFromBlob(blob, originalName, mimeType) {
        const extension = mimeType.split("/")[1] || "jpg";
        const baseName = originalName.replace(/\.[^/.]+$/, "");
        const fileName = `${baseName}.${extension}`;

        return new File([blob], fileName, {
          type: mimeType,
          lastModified: Date.now(),
        });
      }

      async uploadFile(file) {
        const body = new FormData();
        body.append("file", file);

        const response = await fetch(this.uploadUrl, {
          method: "POST",
          body,
        });

        if (!response.ok) {
          throw new Error(`Upload failed (HTTP ${response.status})`);
        }

        const json = await response.json();
        if (!json || typeof json.key !== "string") {
          throw new Error("Upload response missing 'key'.");
        }
        return json.key;
      }

      beginUpload() {
        this.pendingUploads += 1;
        this.setSubmitButtonLoading(true);
      }

      endUpload() {
        this.pendingUploads = Math.max(0, this.pendingUploads - 1);
        if (this.pendingUploads === 0) {
          this.setSubmitButtonLoading(false);
          if (this.submitPending) {
            this.submitPending = false;
            const form = this.getForm();
            const submitButton =
              form && form.querySelector('[type="submit"]');
            if (submitButton) {
              submitButton.removeAttribute("aria-disabled");
              submitButton.click();
            }
          }
        }
      }

      setSubmitButtonLoading(isLoading) {
        const form = this.getForm();
        if (!form) return;
        const submitButton = form.querySelector('[type="submit"]');
        if (!submitButton) return;

        submitButton.toggleAttribute("aria-busy", isLoading);

        const span = submitButton.querySelector("span");
        if (isLoading) {
          submitButton.setAttribute("aria-disabled", "true");
          // Save the original label only on the first transition into the
          // uploading state so subsequent calls don't overwrite it with
          // our own "uploading" text.
          if (span && this.originalSubmitText == null) {
            this.originalSubmitText = span.textContent;
            span.textContent = this.uploadingText;
          }
        } else if (this.pendingUploads === 0) {
          submitButton.removeAttribute("aria-disabled");
          if (span && this.originalSubmitText != null) {
            span.textContent = this.originalSubmitText;
            this.originalSubmitText = null;
          }
        }
      }
    }
  );
}
